import { Box, Drawer } from '@mui/material'
import { useContext } from 'react'
import { allAvailableEventTypes } from 'src/consumer/constants'
import { normalizeScreeningSpecialtyForPayload } from 'src/consumer/helpers/screeningSpecialtyUtils'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import { checkAndGetTargetValue } from 'src/consumer/helpers/utilFunctions'
import { GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { getMPDataWithoutParams } from 'src/mentor/services'
import BaseAddCustomPriceComponent from '../BaseAddCustomPriceComponent'
import ClinicAssociationSelector from './formSections/ClinicAssociationSelector'
import DurationAndBuffers from './formSections/DurationAndBuffers'
import EventBasicFields from './formSections/EventBasicFields'
import EventFormHeader from './formSections/EventFormHeader'
import EventTypeConfiguration from './formSections/EventTypeConfiguration'
import FormActionButtons from './formSections/FormActionButtons'
import LocationConfiguration from './formSections/LocationConfiguration'
import PanelAndWebinarConfig from './formSections/PanelAndWebinarConfig'
import PricingConfiguration from './formSections/PricingConfiguration'
import SessionOptionsCheckboxes from './formSections/SessionOptionsCheckboxes'
import useEventFormState from './hooks/useEventFormState'

export default function EventTypeCreationForm({
  eventFormModalData,
  setEventFormModalOpen,
  displayNamesOfEventTypes,
  handleEventCreation,
  oneOnOnePlans,
  bundlePacks,
  refreshBundlePacks,
  refreshOneOnOnePlans,
  saving,
  mentorProfileData,
  mentorClinicAssociationData,
  forceShowTherapistEventTypes = false,
  saveOneOnOnePlanApi,
  forceShowDoctorEventTypes = false,
  showNextVisitSlotSelectorEnabler = false,
  isDisabled = false,
  layout = 'modal',
}) {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)

  const {
    basicEventObject,
    setBasicEventObject,
    selectedPanelMembers,
    setSelectedPanelMembers,
    isCustomTimeChosen,
    setIsCustomTimeChosen,
    viewPriceCountry,
    setViewPriceCountry,
    showAddCustomPrice,
    setShowAddCustomPrice,
    oneOnOnePlanToEdit,
    setOneOnOnePlanToEdit,
    changeLocationBasedOnMentorOrClinicLocation,
  } = useEventFormState({
    eventFormModalData,
    oneOnOnePlans,
    mentorClinicAssociationData,
    mentorProfileData,
  })

  function saveEvent() {
    let eventObjectToSend = { ...basicEventObject }
    if (eventObjectToSend?.type === allAvailableEventTypes?.assessment) {
      const ids = (eventObjectToSend?.psychAssessmentIds || [])
        .map((entry) => entry?.id ?? entry?.value)
        .filter(Boolean)
      eventObjectToSend.psychAssessmentIds = ids
      eventObjectToSend.clinicalAssessmentIds = ids
    }
    if (eventObjectToSend?.type === allAvailableEventTypes?.screening) {
      const slug = normalizeScreeningSpecialtyForPayload(eventObjectToSend.targetSpecialtySlug)
      if (slug) {
        eventObjectToSend.targetSpecialtySlug = slug
      } else {
        delete eventObjectToSend.targetSpecialtySlug
        delete eventObjectToSend.screeningType
      }
    }
    handleEventCreation(eventObjectToSend)
  }

  const handleValidation = (data) => {
    if (!data?.title) {
      return false
    }
    if (data?.type !== 'webinar') {
      if (!(parseInt(data?.durationInMins, 10) > 2)) {
        return false
      }
    } else {
      const maxUsers = parseInt(data?.maxUsers, 10)
      const minUsers = parseInt(data?.minUsers, 10)
      if (maxUsers < minUsers) {
        return false
      }
      if (maxUsers < 1) {
        return false
      }
      if (minUsers < 0) {
        return false
      }
    }
    if (!data?.free) {
      const planId =
        data?.type === allAvailableEventTypes.webinar
          ? data?.pricingPlanId || data?.subscriptionPlanId
          : data?.subscriptionPlanId
      if (!planId) {
        return false
      }
    }
    return true
  }

  function handleChangeLocation(event) {
    const { id } = event.target
    setBasicEventObject((prev) => ({
      ...prev,
      location: { ...(prev?.location || {}), [id]: checkAndGetTargetValue(event) },
    }))
  }

  function handleEditOneOnOnePlan() {
    const foundPlan = oneOnOnePlans?.find(
      (item) => item?.id === basicEventObject?.subscriptionPlanId,
    )
    setOneOnOnePlanToEdit(foundPlan)
    setShowAddCustomPrice(true)
  }

  function changeCountry(selectValue) {
    setBasicEventObject((prev) => ({
      ...prev,
      location: { ...prev?.location, countryCode: selectValue?.id, country: selectValue?.name },
    }))
  }

  function changePlan(e) {
    if (e.target.value !== 'create-plan') {
      basicEventObject?.type !== allAvailableEventTypes?.webinar
        ? setBasicEventObject((prev) => ({ ...prev, subscriptionPlanId: e.target.value }))
        : setBasicEventObject((prev) => ({ ...prev, pricingPlanId: e.target.value }))
    } else {
      setOneOnOnePlanToEdit()
      setShowAddCustomPrice(true)
    }
  }

  function handleCloseAddCustomPrice() {
    setShowAddCustomPrice(false)
  }

  function setBundle(value) {
    setBasicEventObject((prev) => {
      if (typeof value === 'function') {
        return { ...prev, bundlePlanIds: value(prev?.bundlePlanIds || []) }
      } else {
        return { ...prev, bundlePlanIds: { ...(prev.bundlePlanIds || []), ...value } }
      }
    })
  }

  async function loadOptions(search, loadedOptions, { page }) {
    const response = await getMPDataWithoutParams(
      `/api/secure/teacher/other-interviewers/list?search=${search}&page=${page}`,
    )
    var hasMore = true
    var optionsArray
    if (response?.status === 200) {
      optionsArray = response?.data?.map((option) => {
        return {
          id: option?.id,
          name: option?.name || option?.firstName,
          profilePicId: option?.profilePicId,
        }
      })
    }
    if (response?.status !== 200 || !optionsArray?.length) {
      hasMore = false
    }

    return {
      options: optionsArray,
      hasMore,
      additional: {
        page: page + 1,
      },
    }
  }

  const handleMultiSelectPanelMembersChange = (value) => {
    setBasicEventObject((prev) => ({
      ...prev,
      panelIds: value.map((val) => val.id),
    }))
    setSelectedPanelMembers(value)
  }

  const formSections = (
    <>
      {layout === 'modal' && (
        <EventFormHeader
          eventFormModalData={eventFormModalData}
          onClose={() => setEventFormModalOpen(false)}
          isDisabled={isDisabled}
        />
      )}

      <EventBasicFields
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        isDisabled={isDisabled}
      />

      <EventTypeConfiguration
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        displayNamesOfEventTypes={displayNamesOfEventTypes}
        mentorProfileData={mentorProfileData}
        forceShowDoctorEventTypes={forceShowDoctorEventTypes}
        forceShowTherapistEventTypes={forceShowTherapistEventTypes}
        changeLocationBasedOnMentorOrClinicLocation={changeLocationBasedOnMentorOrClinicLocation}
        isDisabled={isDisabled}
      />

      <PanelAndWebinarConfig
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        selectedPanelMembers={selectedPanelMembers}
        handleMultiSelectPanelMembersChange={handleMultiSelectPanelMembersChange}
        loadOptions={loadOptions}
        isDisabled={isDisabled}
      />

      <DurationAndBuffers
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        isCustomTimeChosen={isCustomTimeChosen}
        setIsCustomTimeChosen={setIsCustomTimeChosen}
        isDisabled={isDisabled}
      />

      <SessionOptionsCheckboxes
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        oneOnOnePlans={oneOnOnePlans}
        showNextVisitSlotSelectorEnabler={showNextVisitSlotSelectorEnabler}
        isDisabled={isDisabled}
      />

      <PricingConfiguration
        basicEventObject={basicEventObject}
        viewPriceCountry={viewPriceCountry}
        setViewPriceCountry={setViewPriceCountry}
        oneOnOnePlans={oneOnOnePlans}
        bundlePacks={bundlePacks}
        unSecureSetupData={unSecureSetupData}
        changePlan={changePlan}
        handleEditOneOnOnePlan={handleEditOneOnOnePlan}
        setBundle={setBundle}
        refreshBundlePacks={refreshBundlePacks}
        mentorProfileData={mentorProfileData}
        isDisabled={isDisabled}
      />

      <LocationConfiguration
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        handleChangeLocation={handleChangeLocation}
        changeCountry={changeCountry}
        unSecureSetupData={unSecureSetupData}
        isDisabled={isDisabled}
      />

      <ClinicAssociationSelector
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        mentorClinicAssociationData={mentorClinicAssociationData}
        changeLocationBasedOnMentorOrClinicLocation={changeLocationBasedOnMentorOrClinicLocation}
        isDisabled={isDisabled}
      />
    </>
  )

  const actionBar =
    !isDisabled && (
      <FormActionButtons
        onCancel={() => setEventFormModalOpen(false)}
        onSave={saveEvent}
        saving={saving}
        isValid={handleValidation(basicEventObject)}
        isEdit={Boolean(eventFormModalData) || !!eventFormModalData?.id}
        isDisabled={isDisabled}
        fullWidthActions={layout === 'drawer'}
      />
    )

  return (
    <>
      {layout === 'drawer' ? (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              px: { xs: 2, sm: 2.5 },
              py: 2,
            }}
          >
            <GenericDisplayFlexColumn style={{ gap: '22px', maxWidth: '100%' }}>
              {formSections}
            </GenericDisplayFlexColumn>
          </Box>
          {actionBar && (
            <Box
              sx={{
                flexShrink: 0,
                px: { xs: 2, sm: 2.5 },
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: (theme) => theme.palette.grey[50],
              }}
            >
              {actionBar}
            </Box>
          )}
        </>
      ) : (
        <GenericDisplayFlexColumn style={{ gap: '24px', maxWidth: '420px' }}>
          {formSections}
          {actionBar}
        </GenericDisplayFlexColumn>
      )}

      <Drawer
        anchor="right"
        open={showAddCustomPrice}
        onClose={handleCloseAddCustomPrice}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 'min(100vw, 520px)', md: 560 },
            maxWidth: '100vw',
          },
        }}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      >
        <BaseAddCustomPriceComponent
          selectedPlan={oneOnOnePlanToEdit}
          handleChangePlan={changePlan}
          closeFunction={handleCloseAddCustomPrice}
          refreshOneOnOnePlans={refreshOneOnOnePlans}
          saveOneOnOnePlanApi={saveOneOnOnePlanApi}
          mentorProfileData={mentorProfileData}
        />
      </Drawer>
    </>
  )
}
